import { useNavigate } from "react-router-dom";
import { isProjectAdmin, getProjectRole, isGlobalEditor } from "../utils/permissions";
import { NotificationBell } from "./NotificationBell";
import UserMenu from "./UserMenu";

type ActiveTab = "dashboard" | "report" | "history" | "members" | "project";

interface Props {
  projectId: string;
  active: ActiveTab;
}

export default function ProjectNav({ projectId, active }: Props) {
  const navigate = useNavigate();
  const role = getProjectRole(projectId);
  const isAdmin = isProjectAdmin(projectId);
  const isViewer = role === "viewer";
  const isEditor = isGlobalEditor();

  const tab = (label: string, id: ActiveTab, onClick: () => void, show = true) => {
    if (!show) return null;
    const isActive = active === id;
    return (
      <button
        key={id}
        onClick={onClick}
        className={
          isActive
            ? "px-4 py-1.5 bg-blue-600 text-white rounded-full text-sm font-medium"
            : "px-4 py-1.5 text-gray-600 text-sm hover:text-gray-900"
        }
      >
        {label}
      </button>
    );
  };

  return (
    <nav className="bg-white border-b border-gray-200 px-6 py-2.5 flex items-center justify-between">
      {/* Logo */}
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        <div>
          <div className="text-xs text-gray-400 leading-none">COLLINS</div>
          <div className="text-sm font-semibold text-gray-900 leading-tight">EAC Reporting</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1">
        {tab("Dashboard",       "dashboard", () => navigate(`/dashboard/${projectId}`),             !isViewer)}
        {tab("Report",          "report",    () => navigate(`/upload/${projectId}`),                !isViewer && !isEditor)}
        {tab("History",         "history",   () => navigate("/reports"))}
        {tab("User Management", "members",   () => navigate(`/projects/${projectId}/members`),      isAdmin)}
        {tab("Project",         "project",   () => navigate(`/settings/project/${projectId}`),      isAdmin)}
      </div>

      {/* Right icons */}
      <div className="flex items-center gap-3">
        <button className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </button>
        <NotificationBell />
        <UserMenu />
      </div>
    </nav>
  );
}
